import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stamp, Gift, Loader2, AlertCircle, MapPin, Lock, Navigation, CheckCircle2, Phone, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp Business number for Stampply
const WHATSAPP_NUMBER = "34683561032"; // Format: country code + number without +

const StampPage = () => {
  const { deviceId } = useParams();
  const [status, setStatus] = useState('loading'); // loading, checking_location, location_denied, pin_required, ready, confirming, success, error
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [stampResult, setStampResult] = useState(null);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [pin, setPin] = useState('');
  const [phone, setPhone] = useState('');
  const [validatingPin, setValidatingPin] = useState(false);
  const [validatingLocation, setValidatingLocation] = useState(false);
  const [confirmingStamp, setConfirmingStamp] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(true);

  useEffect(() => {
    loadDeviceInfo();
  }, [deviceId]);

  const loadDeviceInfo = async () => {
    try {
      setStatus('loading');
      const response = await axios.get(`${API}/stamp/info/${deviceId}`);
      setDeviceInfo(response.data);
      
      // Check if geolocation validation is required
      if (response.data.requires_geolocation) {
        // Show button to activate location instead of auto-requesting
        setStatus('request_location');
      } else {
        // No geolocation required, generate token directly (legacy flow)
        await generateTokenLegacy();
      }
    } catch (error) {
      console.error('Error loading device info:', error);
      setError(error.response?.data?.detail || 'No se pudo cargar la información');
      setStatus('error');
    }
  };

  const requestGeolocation = () => {
    setValidatingLocation(true);
    setStatus('checking_location');
    
    if (!navigator.geolocation) {
      setLocationError('Tu navegador no soporta geolocalización. Usa el PIN de respaldo.');
      setStatus('pin_required');
      setValidatingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await validateGeolocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMsg = 'No pudimos obtener tu ubicación.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Has denegado el acceso a tu ubicación. Usa el PIN de respaldo.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'La ubicación no está disponible. Usa el PIN de respaldo.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Tiempo de espera agotado. Usa el PIN de respaldo.';
            break;
          default:
            break;
        }
        
        setLocationError(errorMsg);
        setStatus('pin_required');
        setValidatingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const validateGeolocation = async (latitude, longitude) => {
    try {
      const response = await axios.post(`${API}/stamp/validate/geolocation`, {
        device_id: deviceId,
        latitude,
        longitude
      });

      if (response.data.valid) {
        setTokenData(response.data);
        setStatus('ready');
      } else {
        setLocationError(response.data.message);
        setStatus('location_denied');
      }
    } catch (error) {
      console.error('Error validating geolocation:', error);
      setLocationError(error.response?.data?.detail || 'Error al validar ubicación');
      setStatus('pin_required');
    } finally {
      setValidatingLocation(false);
    }
  };

  const validatePin = async () => {
    if (pin.length < 4) {
      return;
    }

    setValidatingPin(true);
    try {
      const response = await axios.post(`${API}/stamp/validate/pin`, {
        device_id: deviceId,
        pin
      });

      if (response.data.valid) {
        setTokenData(response.data);
        setStatus('ready');
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      console.error('Error validating PIN:', error);
      setError(error.response?.data?.detail || 'Error al validar PIN');
    } finally {
      setValidatingPin(false);
    }
  };

  // Legacy flow for devices without location
  const generateTokenLegacy = async () => {
    try {
      setStatus('loading');
      const response = await axios.get(`${API}/stamp/token/${deviceId}`);
      setTokenData(response.data);
      setStatus('ready');
    } catch (error) {
      console.error('Error generating token:', error);
      setError(error.response?.data?.detail || 'No se pudo generar el token');
      setStatus('error');
    }
  };

  const confirmStamp = async () => {
    if (!phone || phone.length < 9) {
      setError('Introduce tu número de teléfono completo');
      return;
    }

    setConfirmingStamp(true);
    setError('');
    
    try {
      // Format phone: add +34 if not present
      let formattedPhone = phone.replace(/\s/g, '');
      if (!formattedPhone.startsWith('+')) {
        if (formattedPhone.startsWith('34')) {
          formattedPhone = '+' + formattedPhone;
        } else {
          formattedPhone = '+34' + formattedPhone;
        }
      }

      const response = await axios.post(`${API}/stamp/confirm`, {
        token: tokenData.token,
        phone: formattedPhone
      });

      setStampResult(response.data);
      // Check if this is a new customer (first stamp = first visit)
      setIsFirstVisit(response.data.stamps <= response.data.stamps_added);
      setStatus('success');
    } catch (error) {
      console.error('Error confirming stamp:', error);
      setError(error.response?.data?.detail || 'Error al registrar el sello');
    } finally {
      setConfirmingStamp(false);
    }
  };

  // Generate WhatsApp link with predefined message
  const getWhatsAppLink = () => {
    const message = encodeURIComponent("Hola Stampply, quiero mi regalo 👋");
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  // Build full logo URL
  const getMerchantLogoUrl = () => {
    const logo = tokenData?.merchant_logo || deviceInfo?.merchant_logo;
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    return `${BACKEND_URL}${logo}`;
  };

  const retryGeolocation = () => {
    setLocationError('');
    setStatus('checking_location');
    requestGeolocation();
  };

  // Loading State
  if (status === 'loading') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin mb-4" />
          <p className="text-slate-600">Cargando...</p>
        </motion.div>
      </div>
    );
  }

  // Request Location State - Ask user to enable location
  if (status === 'request_location') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Merchant Logo */}
          {deviceInfo?.merchant_logo && (
            <div className="flex justify-center mb-4">
              <img 
                src={deviceInfo.merchant_logo.startsWith('http') ? deviceInfo.merchant_logo : `${BACKEND_URL}${deviceInfo.merchant_logo}`}
                alt={deviceInfo?.merchant_name || 'Logo'} 
                className="h-20 max-w-[180px] object-contain"
              />
            </div>
          )}

          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">
            Verifica tu ubicación
          </h1>
          
          <p className="text-slate-600 text-center mb-4">
            Para registrar tu visita en <strong>{deviceInfo?.merchant_name || 'este comercio'}</strong>, necesitamos verificar que estés en el local.
          </p>

          {deviceInfo?.location?.name && (
            <div className="bg-slate-50 rounded-xl p-3 mb-6 text-center">
              <p className="text-sm text-slate-500">Local:</p>
              <p className="font-medium text-slate-900">{deviceInfo.location.name}</p>
            </div>
          )}

          <div className="space-y-3">
            <Button 
              onClick={requestGeolocation} 
              className="w-full btn-primary text-lg py-6"
              disabled={validatingLocation}
            >
              {validatingLocation ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Navigation className="w-6 h-6" />
                  Activar ubicación
                </>
              )}
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              Tu navegador te pedirá permiso para acceder a tu ubicación
            </p>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">o si tienes problemas</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setStatus('pin_required')}
            >
              <Lock className="w-5 h-5" />
              Usar PIN del establecimiento
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Checking Location State
  if (status === 'checking_location') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Navigation className="w-10 h-10 text-blue-500 animate-pulse" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Verificando ubicación</h1>
          <p className="text-slate-600 mb-4">
            Estamos comprobando que estés en el establecimiento
          </p>
          {deviceInfo?.location?.name && (
            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-500">Local:</p>
              <p className="font-medium text-slate-900">{deviceInfo.location.name}</p>
            </div>
          )}
          <Loader2 className="w-8 h-8 text-primary mx-auto animate-spin" />
        </motion.div>
      </div>
    );
  }

  // Location Denied State (too far)
  if (status === 'location_denied') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">Estás demasiado lejos</h1>
          <p className="text-slate-600 text-center mb-6">{locationError}</p>
          
          <div className="space-y-3">
            <Button onClick={retryGeolocation} className="w-full btn-primary">
              <Navigation className="w-5 h-5" /> Reintentar ubicación
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setStatus('pin_required')}
            >
              <Lock className="w-5 h-5" /> Usar PIN de respaldo
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PIN Required State
  if (status === 'pin_required') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">Validación con PIN</h1>
          <p className="text-slate-600 text-center mb-2">
            Pide al personal del establecimiento que introduzca el PIN
          </p>
          
          {locationError && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-700">
              {locationError}
            </div>
          )}

          <div className="space-y-4 mt-6">
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Introduce el PIN"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
              data-testid="pin-input"
            />
            
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}

            <Button 
              onClick={validatePin} 
              className="w-full btn-primary"
              disabled={pin.length < 4 || validatingPin}
            >
              {validatingPin ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" /> Validar PIN
                </>
              )}
            </Button>

            <Button 
              variant="ghost" 
              className="w-full text-slate-500"
              onClick={retryGeolocation}
            >
              <Navigation className="w-4 h-4" /> Reintentar con geolocalización
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error State
  if (status === 'error') {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Ups!</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={loadDeviceInfo} className="btn-primary">
            Intentar de nuevo
          </Button>
        </motion.div>
      </div>
    );
  }

  // Success State
  if (status === 'success' && stampResult) {
    const merchantLogo = getMerchantLogoUrl();
    
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Success Animation */}
          <motion.div 
            className={`w-20 h-20 ${stampResult.goal_reached ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-4`}
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.5 }}
          >
            {stampResult.goal_reached ? (
              <PartyPopper className="w-10 h-10 text-amber-500" />
            ) : (
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            )}
          </motion.div>

          {merchantLogo && (
            <div className="flex justify-center mb-4">
              <img 
                src={merchantLogo} 
                alt={tokenData?.merchant_name || 'Logo'} 
                className="h-24 max-w-[200px] object-contain"
              />
            </div>
          )}

          <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
            {stampResult.goal_reached ? '🎉 ¡Felicidades!' : '✅ ¡Sello registrado!'}
          </h1>

          {stampResult.goal_reached ? (
            <div className="text-center">
              <p className="text-slate-600 mb-4">
                Has completado tu tarjeta y ganado tu premio
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <Gift className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="font-bold text-amber-900">{stampResult.reward}</p>
              </div>
              
              {/* WhatsApp Button for Voucher - Always show to ensure they receive it */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-green-800 mb-3">
                  📱 Para recibir tu voucher por WhatsApp:
                </p>
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-xl transition-colors w-full"
                  data-testid="whatsapp-voucher-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Activar notificaciones WhatsApp
                </a>
                <p className="text-xs text-green-600 mt-2">
                  Pulsa, envía el mensaje y recibirás tu premio
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-slate-600 mb-4">
                {stampResult.double_stamp && <span className="text-amber-600 font-medium">¡Doble sello! </span>}
                Llevas {stampResult.stamps}/{stampResult.goal} sellos
              </p>
              
              {/* Progress Bar */}
              <div className="bg-slate-100 rounded-full h-4 mb-4 overflow-hidden">
                <motion.div 
                  className="bg-primary h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(stampResult.stamps / stampResult.goal) * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>

              <div className="bg-slate-50 rounded-xl p-3 mb-4">
                <p className="text-sm text-slate-500">Te faltan</p>
                <p className="text-2xl font-bold text-primary">{stampResult.remaining} sellos</p>
                <p className="text-sm text-slate-500">para: <span className="font-medium text-slate-700">{stampResult.reward}</span></p>
              </div>

              {/* WhatsApp activation for first-time visitors */}
              {isFirstVisit ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-800 mb-3">
                    📱 <strong>¡Activa WhatsApp!</strong> Recibirás tus sellos y premios directamente
                  </p>
                  <a
                    href={getWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-xl transition-colors w-full"
                    data-testid="whatsapp-activate-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Activar notificaciones
                  </a>
                  <p className="text-xs text-green-600 mt-2">
                    Solo tienes que hacerlo una vez
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-sm text-blue-700">
                    💡 Envía <strong>PROGRESO</strong> por WhatsApp para consultar tus sellos en cualquier momento
                  </p>
                </div>
              )}
            </div>
          )}

        </motion.div>
      </div>
    );
  }

  // Ready State (Token generated - ask for phone)
  const merchantLogo = getMerchantLogoUrl();

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
      <motion.div 
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Success indicator for geolocation */}
        {tokenData?.distance !== undefined && (
          <motion.div 
            className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-700">
              Ubicación verificada ({tokenData.distance}m del local)
            </span>
          </motion.div>
        )}

        {/* Merchant Logo Header */}
        {merchantLogo && (
          <div className="flex justify-center">
            <img 
              src={merchantLogo} 
              alt={tokenData?.merchant_name || 'Logo'} 
              className="h-32 max-w-[220px] object-contain"
            />
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-4">
          {!merchantLogo && (
            <motion.div 
              className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <Stamp className="w-10 h-10 text-primary" />
            </motion.div>
          )}
          <h1 className="text-2xl font-bold text-slate-900">¡Registra tu visita!</h1>
          <p className="text-slate-600 mt-1">
            {tokenData?.merchant_name && <span className="font-medium">{tokenData.merchant_name}</span>}
            {tokenData?.merchant_name && tokenData?.campaign_name && <span> · </span>}
            {tokenData?.campaign_name}
          </p>
        </div>

        {/* Progress Card */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-600">Premio</span>
            <span className="text-sm text-slate-500">Meta: {tokenData?.goal} sellos</span>
          </div>
          <div className="flex items-center gap-2 text-secondary">
            <Gift className="w-5 h-5" />
            <span className="font-medium">{tokenData?.reward}</span>
          </div>
          {tokenData?.reward_description && (
            <p className="text-sm text-slate-500 mt-2 italic">
              {tokenData.reward_description}
            </p>
          )}
        </div>

        {/* Phone Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-red-600 mb-2">
              Tu número de WhatsApp *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="612 345 678"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value.replace(/[^\d\s+]/g, ''));
                  setError('');
                }}
                className="pl-11 text-lg border-red-300 focus:border-red-500 focus:ring-red-500/20"
                data-testid="phone-input"
              />
            </div>
            <p className="text-xs text-red-500 mt-1 font-medium">
              ⚠️ Obligatorio para recibir tu sello y premios por WhatsApp
            </p>
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}

          <Button 
            className="w-full btn-primary text-lg py-6"
            onClick={confirmStamp}
            disabled={confirmingStamp || phone.length < 9}
            data-testid="confirm-stamp-btn"
          >
            {confirmingStamp ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Stamp className="w-6 h-6" />
                Registrar mi sello
              </>
            )}
          </Button>
        </div>

        {/* Token info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-slate-400">
            Validación expira en 90 segundos
          </p>
        </div>

        {/* Refresh Token */}
        <div className="mt-4 text-center">
          <button 
            onClick={loadDeviceInfo}
            className="text-sm text-primary hover:underline"
          >
            ¿Expiró? Valida tu ubicación de nuevo
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default StampPage;
